import { createApp } from "@backstage/app-defaults";
import { AppRouter, FlatRoutes } from "@backstage/core-app-api";
import {
  AlertDisplay,
  OAuthRequestDialog,
  ProxiedSignInPage,
  SignInPage,
} from "@backstage/core-components";
import {
  configApiRef,
  githubAuthApiRef,
  useApi,
} from "@backstage/core-plugin-api";
import { apiDocsPlugin, ApiExplorerPage } from "@backstage/plugin-api-docs";
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from "@backstage/plugin-catalog";
import { catalogLocationReadPermission } from "@backstage/plugin-catalog-common/alpha";
import { CatalogGraphPage } from "@backstage/plugin-catalog-graph";
import {
  CatalogImportPage,
  catalogImportPlugin,
} from "@backstage/plugin-catalog-import";
import { orgPlugin } from "@backstage/plugin-org";
import { RequirePermission } from "@backstage/plugin-permission-react";
import { ScaffolderPage } from "@backstage/plugin-scaffolder";
import { taskCreatePermission } from "@backstage/plugin-scaffolder-common/alpha";
import { SearchPage } from "@backstage/plugin-search";
import { SignalsDisplay } from "@backstage/plugin-signals";
import { UserSettingsPage } from "@backstage/plugin-user-settings";
import { RbacPage } from "@backstage-community/plugin-rbac";
import { policyEntityCreatePermission } from "@backstage-community/plugin-rbac-common";
import { CloudHealthExplorerPage } from "@prisamedia/plugin-cloudhealth-explorer";
import { cloudhealthExplorerReadPermission } from "@prisamedia/plugin-cloudhealth-explorer-common";
import type { ReactElement } from "react";
import { Navigate, Route, useLocation } from "react-router-dom";

import { apis } from "./apis";
import { isAppProxyActive } from "./appProxy";
import { entityPage } from "./components/catalog/EntityPage";
import { Root } from "./components/Root";
import { searchPage } from "./components/search/SearchPage";
import {
  AppProxyAwareNotFoundPage,
  AppProxyRemountGate,
  PermissionGateErrorPage,
  SessionExpiredDialog,
  SessionExpiredScreen,
} from "./components/SessionExpired";
import { TaskListPage } from "./components/tasklist/TaskListPage";

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    // 404 con comprobación real de sesión cuando se navega via
    // Azure AD / Entra Application Proxy (ver PR #351).
    NotFoundErrorPage: AppProxyAwareNotFoundPage,
    SignInPage: (props) => {
      const configApi = useApi(configApiRef);
      const environment = configApi.getOptionalString("auth.environment");
      const localGuestAuth =
        configApi.getOptionalBoolean("app.localGuestAuth") === true &&
        environment === "development" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1");

      // Si se accede vía Azure AD Application Proxy (flag app.oauth2ProxyEnabled
      // y origin actual igual al app.baseUrl público del entorno), las cabeceras
      // HTTP ya contienen la identidad del usuario. Usamos ProxiedSignInPage para
      // autenticación transparente sin login.
      if (isAppProxyActive(configApi, window.location.origin)) {
        return (
          <ProxiedSignInPage
            {...props}
            provider="oauth2Proxy"
            ErrorComponent={SessionExpiredScreen}
          />
        );
      }

      // Acceso directo: GitHub en entornos reales; guest solo en E2E o cuando
      // app-config.local.yaml activa explícitamente localGuestAuth.
      const oauthProviders = [
        {
          id: "github-auth-provider",
          title: "GitHub",
          message: "Sign in using GitHub",
          apiRef: githubAuthApiRef,
        },
      ];

      return (
        <SignInPage
          {...props}
          auto
          providers={
            localGuestAuth || environment === "e2e"
              ? ["guest", ...oauthProviders]
              : oauthProviders
          }
        />
      );
    },
  },
});

export function hasUsableFormData(search: string): boolean {
  const params = new URLSearchParams(search);
  const formDataRaw = params.get("formData");
  if (!formDataRaw) {
    return false;
  }
  try {
    const parsed = JSON.parse(formDataRaw);
    return (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed) &&
      Object.keys(parsed).length > 0
    );
  } catch {
    return false;
  }
}

export function EditSaasRouteGuard({ children }: { children: ReactElement }) {
  const location = useLocation();
  if (
    location.pathname === "/create/templates/default/edit-saas" &&
    !hasUsableFormData(location.search)
  ) {
    return <Navigate to="/create" replace />;
  }
  return children;
}

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/catalog-import" element={<CatalogImportPage />} />
    {/* NOTE: catalogLocationReadPermission es un BasicPermission que controla
        "locations" (fuentes de ingesta), no entity browsing. Se usa aquí porque
        catalogEntityReadPermission es ResourcePermission (requiere entityRef)
        y no sirve para gates a nivel de ruta. Ver DEVOPS-209. */}
    <Route
      path="/catalog"
      element={
        <RequirePermission
          permission={catalogLocationReadPermission}
          errorPage={<PermissionGateErrorPage />}
        >
          <CatalogIndexPage />
        </RequirePermission>
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={
        <RequirePermission
          permission={catalogLocationReadPermission}
          errorPage={<PermissionGateErrorPage />}
        >
          <CatalogEntityPage />
        </RequirePermission>
      }
    >
      {entityPage}
    </Route>
    <Route
      path="/create"
      element={
        <RequirePermission
          permission={taskCreatePermission}
          errorPage={<PermissionGateErrorPage />}
        >
          <EditSaasRouteGuard>
            <ScaffolderPage
              groups={[
                {
                  title: "Catálogo",
                  filter: (entity) =>
                    Boolean(entity.metadata.tags?.includes("catalog")),
                },
                {
                  title: "GitHub",
                  filter: (entity) =>
                    Boolean(entity.metadata.tags?.includes("github")),
                },
                {
                  title: "Automatización",
                  filter: (entity) =>
                    Boolean(entity.metadata.tags?.includes("automation")),
                },
              ]}
              headerOptions={{
                title: "Gestionar catálogo",
                subtitle:
                  "Crea o actualiza componentes, sistemas y fichas SaaS del catálogo mediante flujos guiados",
              }}
              templateFilter={(entity) => entity.metadata.name !== "edit-saas"}
            />
          </EditSaasRouteGuard>
        </RequirePermission>
      }
    />
    <Route
      path="/tasklist"
      element={
        <RequirePermission
          permission={taskCreatePermission}
          errorPage={<PermissionGateErrorPage />}
        >
          <TaskListPage />
        </RequirePermission>
      }
    />

    <Route
      path="/rbac"
      element={
        <RequirePermission
          permission={policyEntityCreatePermission}
          errorPage={<PermissionGateErrorPage />}
        >
          <RbacPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route
      path="/cloud-explorer"
      element={
        <RequirePermission
          permission={cloudhealthExplorerReadPermission}
          errorPage={<PermissionGateErrorPage />}
        >
          <CloudHealthExplorerPage />
        </RequirePermission>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <SessionExpiredDialog />
    {/* Remount controlado (máx. 1 por carga) para identidad stale de
        ProxiedSignInPage tras un probe App Proxy "valid": sustituye al
        location.reload. Ver PermissionGateRemount.tsx. */}
    <AppProxyRemountGate>
      <AppRouter>
        <Root>{routes}</Root>
      </AppRouter>
    </AppProxyRemountGate>
  </>,
);
